import useInvestAssets from 'src/common/apollo/hooks/useInvestAssets'
import useLockedAmountsInOTC from 'src/common/apollo/hooks/useLockedAmountsInOTC'
import useOwnedGoldTokens from 'src/common/apollo/hooks/useOwnedGoldTokens'
import { useAllPriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import {
  getPriceFromPriceFeed,
  injectPricingData,
} from 'src/common/queries/usePriceFeeds/utils'
import { useAllTokensForOwner } from 'src/common/queries/useTokensForOwner/useAllTokensForOwner'
import {
  OwnedERC20,
  OwnedLockedToken,
  OwnedToken,
  TokenWithBalances,
} from 'src/common/types/models/Tokens'
import { ZERO } from 'src/common/utils/big-utils'
import { mapBy } from 'src/common/utils/collections/mapBy'

import differenceBy from 'lodash/differenceBy'
import isNil from 'lodash/isNil'

import { values, mergeWith, keyBy } from 'lodash'
import { sortTokensByBalanceValue } from 'src/common/utils/collections/sortByBalance'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import Big from 'big.js'
import { useMemo } from 'react'
import useCarbon from 'src/common/queries/useCarbon'
import useOwnedCarbonTokens from 'src/common/apollo/hooks/useOwnedCarbonTokens'

const calcLockedBalances = (
  tokens: OwnedToken[],
  tokensWithLockedAmountDictionary: Map<string, OwnedLockedToken>
): TokenWithBalances[] =>
  tokens.map(asset => ({
    ...asset,
    lockedAmount:
      tokensWithLockedAmountDictionary.get(asset.id)?.lockedAmount ?? ZERO,
    usdLockedAmount: tokensWithLockedAmountDictionary
      .get(asset.id)
      ?.lockedAmount?.mul(asset.exchangePrice ?? ZERO),
  }))

const useWalletTokens = () => {
  const { smartAccountInfo } = useSmartAccount()
  const { priceFeeds, isLoading: priceFeedsLoading } = useAllPriceFeeds()

  const { ownedGoldTokens, loading: ownedGoldTokensLoading } =
    useOwnedGoldTokens(smartAccountInfo?.address)
  const { carbon, isLoading: carbonLoading } = useCarbon()

  const { ownedErc20s, isLoading: ownedErc20sLoading } = useAllTokensForOwner(
    smartAccountInfo?.address || ''
  )
  const {
    ownedCarbonTokens: ownedCardbonNft,
    total,
    loading: carbonNftLoading,
  } = useOwnedCarbonTokens(smartAccountInfo?.address)
  const firstCarbonNft = ownedCardbonNft?.[0] ? { ...ownedCardbonNft[0] } : null
  if (firstCarbonNft && total) {
    firstCarbonNft.balance = Big(total)
  }

  const ownedErc20sDictionary = mapBy(ownedErc20s, 'id')

  const {
    tokensWithLockedAmountDictionary,
    tokensWithLockedAmount,
    loading: lockedAmountsLoading,
  } = useLockedAmountsInOTC({
    variables: {
      filter: {
        maker_contains_nocase: smartAccountInfo?.address,
      },
    },
  })

  const { investAssets, loading: investAssetsLoading } = useInvestAssets()

  const carbonWithBalance: OwnedERC20 | null = useMemo(() => {
    if (!carbon) return null
    return {
      ...carbon,
      balance: ownedErc20sDictionary.get(carbon.id)?.balance || ZERO,
    }
  }, [carbon, ownedErc20sDictionary])

  const _carbon = calcLockedBalances(
    carbonWithBalance
      ? [injectPricingData<OwnedERC20>(carbonWithBalance, priceFeeds)]
      : [],
    tokensWithLockedAmountDictionary
  )
  const _carbonNft = calcLockedBalances(
    injectPricingData(firstCarbonNft ? [firstCarbonNft] : [], priceFeeds),
    tokensWithLockedAmountDictionary
  )

  const goldTokens = calcLockedBalances(
    injectPricingData(
      [...ownedGoldTokens, ..._carbon, ..._carbonNft],
      priceFeeds
    ),
    tokensWithLockedAmountDictionary
  )

  const cryptoTokens = calcLockedBalances(
    injectPricingData(
      differenceBy(ownedErc20s, investAssets, 'contractAddress'),
      priceFeeds
    ),
    tokensWithLockedAmountDictionary
  )

  const rwaTokens = calcLockedBalances(
    injectPricingData(
      investAssets
        .map(asset => ownedErc20sDictionary.get(asset.id))
        .filter(asset => !isNil(asset)),
      priceFeeds
    ),
    tokensWithLockedAmountDictionary
  )

  const availableTokens = useMemo(
    () =>
      [...goldTokens, ...cryptoTokens, ...rwaTokens].map(token => ({
        ...token,
        id: token.id.toLowerCase(),
      })),
    [cryptoTokens, goldTokens, rwaTokens]
  )

  const lockedTokens = useMemo(
    () =>
      tokensWithLockedAmount.map(t => {
        const exchangePrice = getPriceFromPriceFeed(
          priceFeeds,
          t.contractAddress
        )
        const usdLockedAmount =
          (exchangePrice &&
            t.lockedAmount &&
            t.lockedAmount.mul(exchangePrice)) ||
          undefined

        return {
          ...t,
          id: t.id.toLocaleLowerCase(),
          usdLockedAmount,
          exchangePrice,
          balance: ZERO,
          usdBalance: ZERO,
        }
      }),
    [priceFeeds, tokensWithLockedAmount]
  )
  const allTokens = useMemo(
    () =>
      values(
        mergeWith(
          keyBy<(typeof availableTokens)[0]>(availableTokens, o =>
            o.contractAddress.toLowerCase()
          ),
          keyBy(lockedTokens, o => o.contractAddress.toLowerCase()),
          (obj1, obj2) => {
            return mergeWith(obj1, obj2, (v1, v2) => {
              if (v1 instanceof Big && v2 instanceof Big) {
                return v1.gt(v2) ? v1 : v2
              }
              if (typeof v1 === 'number' && typeof v2 === 'number') {
                return v1 > v2 ? v1 : v2
              }
            })
          }
        )
      ),
    [availableTokens, lockedTokens]
  )

  return {
    goldTokens: sortTokensByBalanceValue(goldTokens),
    cryptoTokens: sortTokensByBalanceValue(cryptoTokens),
    rwaTokens: sortTokensByBalanceValue(rwaTokens),
    allTokens: sortTokensByBalanceValue(allTokens),
    loading:
      ownedGoldTokensLoading ||
      ownedErc20sLoading ||
      priceFeedsLoading ||
      lockedAmountsLoading ||
      investAssetsLoading ||
      carbonLoading ||
      carbonNftLoading,
  }
}

export default useWalletTokens
